import Enumerable from "linq";
import { IClonable } from "..";
import { ISourceReferable } from "../edit";
import { IEditCommandFactory, IEditCommand, RemoveValueOfArrayCommand, MoveValueOfArrayCommand, SortValueOfArrayCommand, instanceOfIIndexSpecifiable } from "../edit/command";
import { IStructuredDocument, IStructuredPage } from "./IStructuredDocument";
import { IPageStructureEditable } from "./IPageStructureEditable";
import { IObjectEditable } from "./IObjectEditable";
import { AbstractPageEditor } from "./AbstractPageEditor";

export abstract class AbstractPageStructureEditor<
  TDocument extends IStructuredDocument<TPage, TObject>,
  TPage extends IStructuredPage<TObject>,
  TObject,
  TObjectEditor extends IObjectEditable & IEditCommandFactory<TPage, IEditCommand<TPage>> & ISourceReferable<TObject> & IClonable<TObjectEditor>
> extends AbstractPageEditor<TDocument, TPage> implements IPageStructureEditable<TObjectEditor> {
  constructor(source: TPage | AbstractPageStructureEditor<TDocument, TPage, TObject, TObjectEditor>, isNew: boolean = false) {
    super(source, isNew);
    if (source instanceof AbstractPageStructureEditor) {
      this._objects = source._objects;
      this._lastSelectedObject = source._lastSelectedObject;
    } else {
      // _objectsは派生クラスの実装で生成するためここでは生成しない.(派生クラスの初期化はこのタイミングでは終わってないため.)
      this._lastSelectedObject = null;
    }
  }

  private _objects: TObjectEditor[];
  private _lastSelectedObject: TObjectEditor;

  protected createObjects(): TObjectEditor[] {
    return Enumerable.from(this.get(s => s.objects, []))
      .select(o => this.createObject(o))
      .toArray();
  }

  protected abstract createObject(source: TObject): TObjectEditor;

  protected reassignObjects(reassigned: TObjectEditor[]) {
    this._objects = reassigned;
  }  

  getObjects(): TObjectEditor[] {
    if (!this._objects) {
      this._objects = this.createObjects();
    }
    return this._objects;
  }

  updateObject(object: TObjectEditor): void {
    // reconstructでも分からないので_objectsもここで更新する.
    this._objects = Enumerable.from(this._objects)
      .select(o => o.isReferedSameSource(object) ? object : o)
      .toArray();
    this.registerEntityCommand(object.createCommand());
    Object.freeze(object);
  }

  addObject(object: TObjectEditor, index?: number): TObjectEditor {
    // 追加の場合は選択状態を更新したいのでreconstructに任せず自分で更新.
    this.deselectAllObjects();
    const added = this.createObject(object.getSource());
    if (index === undefined || index === null) {
      this._objects.push(added);
    } else if (index <= this._objects.length) {
      this._objects.splice(index, 0, added);
    } else {
      throw new Error("index is out of range.");
    }
    this.selectObjects(added);
    const command = object.createCommand();
    if (index !== undefined && index !== null && instanceOfIIndexSpecifiable(command)) {
      command.setIndex(index);
    }
    this.registerEntityCommand(command);
    Object.freeze(object);
    return added;
  }

  removeObject(object: TObjectEditor): void {
    // _objectsの更新はreconstructに任せる.
    this.registerEntityCommand(new RemoveValueOfArrayCommand(s => s.objects, this.toIndex(object)));
  }

  shiftObject(object: TObjectEditor, offset: number): void {
  // _objectsの更新はreconstructに任せる.
    const from = this.toIndex(object);
    const to = from + offset;
    if ((to < 0) || (this._objects.length <= to)) {
      return;
    }
    this.registerEntityCommand(new MoveValueOfArrayCommand(s => s.objects, from, to));
  }

  sortObjects(compare: (a: TObject, b: TObject) => number): void {
    this.registerEntityCommand(new SortValueOfArrayCommand(s => s.objects, compare));
  }

  private toIndex(object: TObjectEditor): number {
    return Enumerable.from(this._objects)
    .select((o, i) => ({ o, i }))
    .where(p => p.o.isReferedSameSource(object))
    .first()
    .i;
  }

  private cloneObject(source: TObjectEditor, update: (o: TObjectEditor) => void): TObjectEditor {
    const result = source.clone();
    update(result);
    return result;
  }

  selectObjects(...objects: TObjectEditor[]): void {
    const updated = Enumerable.from(objects)
      .select(o => this.cloneObject(o, s => s.select()))
      .toArray();
    for (const u of updated) {
      this.updateObject(u);
    }
    this._lastSelectedObject = updated[updated.length - 1];
  }

  deselectObjects(...objects: TObjectEditor[]): void {
    const updated = Enumerable.from(objects)
      .select(o => this.cloneObject(o, s => s.deselect()))
      .toArray();
    for (const u of updated) {
      this.updateObject(u);
    }
    // 最終選択オブジェクトは何にする？そもそもいる？(多分コンテキストメニューさえなんとかなればいらない.)
    // 最終選択オブジェクトは範囲選択時に使用するため必要であるが、ここで常にnullにするのは困るかも？
    this._lastSelectedObject = null;
  }

  getSelectedObjects(): TObjectEditor[] {
    return Enumerable.from(this._objects)
      .where(o => o.isSelected())
      .toArray();
  }

  getLastSelectedObject(): TObjectEditor {
    return this._lastSelectedObject;
  }

  selectAllObjects(): void {
    this.selectObjects(...Enumerable.from(this._objects).where(o => !o.isSelected()).toArray());
  }

  deselectAllObjects(): void {
    this.deselectObjects(...Enumerable.from(this._objects).where(o => o.isSelected()).toArray());
  }

  rangeSelectionObjects(from: TObjectEditor, to: TObjectEditor): TObjectEditor[] {
    const fromIndex = this.toIndex(from);
    const toIndex = this.toIndex(to);
    return Enumerable.from(this._objects)
      .skip(Math.min(fromIndex, toIndex))
      .take(Math.abs(fromIndex - toIndex) + 1)
      .toArray();
  }
}
